import { DriveEncryptionStatus, OsType } from "@/constants/devices";
import { countriesList, usaStatesList } from "@/constants/countries";
import { i18n } from "@/plugins/i18n";
import { isNewerOrSameVersion } from "@coronet/cui";
import type { DeviceDrive, DeviceOsOrClientVersions } from "@/_store/devices.module";
import type { DeviceVersionItem } from "@/_store/devices-settings.module";

/**
 * Returns whether a drive is eligible for Coro encryption.
 * A drive is suitable when it is non-removable, has a compatible agent version,
 * and its encryption status is either `NOT_ENCRYPTED` or `FAILED`.
 *
 * @param drive - The device drive to evaluate.
 * @param marketingVersion - The installed agent's marketing version string.
 * @returns `true` when the drive can be encrypted by Coro.
 */
export const isSuitableDriveForEncryption = (
  drive: DeviceDrive,
  { marketingVersion }: { marketingVersion: string }
): boolean => {
  const clientVersionWhichSupportsEncryption = "1.7";
  if (![DriveEncryptionStatus.NOT_ENCRYPTED, DriveEncryptionStatus.FAILED].includes(drive.status)) {
    return false;
  }
  if (drive.isRemovable) {
    return false;
  }
  return isNewerOrSameVersion(clientVersionWhichSupportsEncryption, marketingVersion);
};

/**
 * Generates a unique device identifier by combining a cryptographically random
 * 32-bit unsigned integer with the current timestamp.
 *
 * @returns A unique string identifier for the device.
 */
export const generateDeviceId = (): string => {
  return `${window.crypto.getRandomValues(new Uint32Array(1))[0]}${+new Date().getTime()}`;
};

/**
 * Resolves a country or US state name from its ISO / FIPS key.
 * US states are prefixed with "US - " to distinguish them from countries.
 *
 * @param key - ISO 3166-1 alpha-2 country code or US FIPS state code.
 * @returns The human-readable country or state name, or an empty string when unknown.
 */
export function getCountryOrStateName(key: string): string {
  if (usaStatesList[key]) {
    return `US - ${usaStatesList[key]}`;
  }
  if (countriesList[key]) {
    return countriesList[key];
  }
  return "";
}

/**
 * Builds display-ready OS version and client version lists for device filter dropdowns.
 *
 * Each list is grouped by OS type with a subheader item, followed by individual
 * version items. Client versions are sorted in descending order (newest first).
 * When `selectedOsVersion` is provided, client versions are limited to OS types
 * that include that OS version.
 *
 * @param selectedOsVersion - Optional OS version to use as a filter for client versions.
 * @param osVersionsFromStore - Map of OS type → available OS version strings.
 * @param clientVersionsFromStore - Map of OS type → available client version strings.
 * @returns `{ osVersions, clientVersions }` — arrays of grouped, display-ready items.
 */
export const getVersionsList = <T extends { subheader?: true; name: string }>(
  selectedOsVersion: string | null | undefined,
  osVersionsFromStore: DeviceOsOrClientVersions,
  clientVersionsFromStore: DeviceOsOrClientVersions
): { osVersions: T[]; clientVersions: T[] } => {
  const allOsTypes = Object.keys(osVersionsFromStore) as (keyof DeviceOsOrClientVersions)[];

  const filteredOsTypes = selectedOsVersion
    ? allOsTypes.filter((osType) => {
        const osVersionsForType = osVersionsFromStore[osType] ?? [];
        return osVersionsForType.includes(selectedOsVersion);
      })
    : allOsTypes;

  const osVersions: T[] = [];
  const clientVersions: T[] = [];

  allOsTypes.forEach((osType) => {
    if (osVersionsFromStore[osType]?.length) {
      osVersions.push(
        { subheader: true, name: i18n.global.t(`general.osVersion.${osType}`) } as T,
        ...(osVersionsFromStore[osType] ?? []).map((v: string) => ({ name: v }) as T)
      );
    }
  });

  filteredOsTypes.forEach((osType) => {
    if (clientVersionsFromStore?.[osType]?.length) {
      clientVersions.push(
        { subheader: true, name: i18n.global.t(`general.osVersion.${osType}`) } as T,
        ...(clientVersionsFromStore[osType] ?? [])
          .slice()
          .sort()
          .reverse()
          .map((v: string) => ({ name: v }) as T)
      );
    }
  });

  return { osVersions, clientVersions };
};

/**
 * Returns the latest agent version for a given OS type from an array of available versions.
 * Versions are compared using marketing version strings in descending order (newest first).
 *
 * @param availableVersions - Array of device version items to search through.
 * @param osType - The OS type to filter by (e.g. `OsType.WINDOWS`).
 * @returns The latest `DeviceVersionItem` for the specified OS type, or `null` if none exist.
 */
export const getLatestVersionByOsType = (
  availableVersions: DeviceVersionItem[],
  osType: OsType
): DeviceVersionItem | null => {
  const versionsForOsType = availableVersions.filter((version) => version.osType === osType);
  if (versionsForOsType.length === 0) return null;

  return versionsForOsType.sort((a, b) => {
    if (isNewerOrSameVersion(a.marketingVersion, b.marketingVersion)) return 1;
    if (isNewerOrSameVersion(b.marketingVersion, a.marketingVersion)) return -1;
    return 0;
  })[0];
};

/**
 * Returns the latest agent version for each of the three target OS types
 * (Windows, macOS, Linux). OS types with no available versions are excluded.
 *
 * @param availableVersions - Array of device version items to search through.
 * @returns Array of the latest `DeviceVersionItem` per OS type (up to 3 items).
 */
export const getLatestVersionsByOsType = (
  availableVersions: DeviceVersionItem[]
): DeviceVersionItem[] => {
  return [OsType.WINDOWS, OsType.OSX, OsType.LINUX]
    .map((osType) => getLatestVersionByOsType(availableVersions, osType))
    .filter((version): version is DeviceVersionItem => version !== null);
};
